import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff, Mail, Lock, User, Store, Sparkles, ArrowRight, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../lib/auth-context";
import { getErrorMessage } from "../../lib/api";

const FieldIcon = ({ children }) => (
  <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
    {children}
  </div>
);

const PasswordField = ({ id, value, onChange, testid, placeholder = "••••••••", required = true, minLength }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <FieldIcon>
        <Lock className="w-4 h-4" />
      </FieldIcon>
      <Input
        id={id}
        data-testid={testid}
        type={show ? "text" : "password"}
        required={required}
        minLength={minLength}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-12 pl-10 pr-11 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${testid}-toggle`}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition-colors"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const GoogleAuthButton = ({ onAuthed, testid }) => {
  const { googleAuth } = useAuth();
  const [loading, setLoading] = useState(false);

  const login = useGoogleLogin({
    scope: "email profile",
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const u = await googleAuth({ accessToken: tokenResponse.access_token });
        onAuthed(u);
      } catch (err) {
        toast.error("Google sign-in failed", { description: getErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error("Google sign-in failed"),
  });

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => login()}
      disabled={loading}
      className="w-full h-12 flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-700 font-semibold text-sm shadow-sm disabled:opacity-60"
    >
      {loading ? (
        <svg className="w-5 h-5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
      )}
      {loading ? "Signing in…" : "Continue with Google"}
    </button>
  );
};

const LoginForm = ({ onClose, onForgot }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const afterAuth = (u) => {
    toast.success(`Welcome back, ${u.name}!`);
    onClose();
    navigate("/app/listing-generator");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login({ email, password });
      afterAuth(u);
    } catch (err) {
      toast.error("Login failed", { description: getErrorMessage(err, "Check your email and password.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="login-form" className="space-y-4">
      <GoogleAuthButton testid="login-google-button" onAuthed={afterAuth} />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase tracking-wider">
          <span className="bg-white px-3 text-slate-400 font-semibold">or with email</span>
        </div>
      </div>

      <div>
        <Label htmlFor="login-email" className="text-slate-700 font-semibold text-sm">Email</Label>
        <div className="relative mt-1.5">
          <FieldIcon><Mail className="w-4 h-4" /></FieldIcon>
          <Input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password" className="text-slate-700 font-semibold text-sm">Password</Label>
          <button
            type="button"
            onClick={onForgot}
            className="text-xs font-semibold text-purple-700 hover:text-purple-900"
            data-testid="login-forgot-link"
          >
            Forgot?
          </button>
        </div>
        <div className="mt-1.5">
          <PasswordField
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testid="login-password-input"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="login-submit-button"
        className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-lg shadow-purple-600/25"
      >
        {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
      </Button>
    </form>
  );
};

const SignupForm = ({ onClose }) => {
  const [name, setName] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const afterAuth = (u) => {
    toast.success(`Welcome, ${u.name}!`, { description: "Your free trial has been activated. Start generating AI listings!" });
    onClose();
    navigate("/app/listing-generator");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await signup({ name, sellerName, email, password });
      afterAuth(u);
    } catch (err) {
      toast.error("Sign up failed", { description: getErrorMessage(err, "Please check your details and try again.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="signup-form" className="space-y-4">
      <GoogleAuthButton testid="signup-google-button" onAuthed={afterAuth} />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase tracking-wider">
          <span className="bg-white px-3 text-slate-400 font-semibold">or with email</span>
        </div>
      </div>

      <div>
        <Label htmlFor="signup-name" className="text-slate-700 font-semibold text-sm">Name</Label>
        <div className="relative mt-1.5">
          <FieldIcon><User className="w-4 h-4" /></FieldIcon>
          <Input
            id="signup-name"
            data-testid="signup-name-input"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="signup-seller" className="text-slate-700 font-semibold text-sm">
          Seller Name <span className="text-slate-400 font-normal">· Optional</span>
        </Label>
        <div className="relative mt-1.5">
          <FieldIcon><Store className="w-4 h-4" /></FieldIcon>
          <Input
            id="signup-seller"
            data-testid="signup-seller-input"
            type="text"
            value={sellerName}
            onChange={(e) => setSellerName(e.target.value)}
            placeholder="Your store / brand name"
            className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="signup-email" className="text-slate-700 font-semibold text-sm">Email</Label>
        <div className="relative mt-1.5">
          <FieldIcon><Mail className="w-4 h-4" /></FieldIcon>
          <Input
            id="signup-email"
            data-testid="signup-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="signup-password" className="text-slate-700 font-semibold text-sm">Password</Label>
        <div className="mt-1.5">
          <PasswordField
            id="signup-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testid="signup-password-input"
            placeholder="Minimum 6 characters"
            minLength={6}
          />
        </div>
        <p className="mt-1.5 text-xs text-slate-500">Use at least 6 characters.</p>
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="signup-submit-button"
        className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-lg shadow-purple-600/25"
      >
        {loading ? "Creating account…" : (<>Create account <Sparkles className="w-4 h-4 ml-2" /></>)}
      </Button>

      <p className="text-[11px] text-slate-500 text-center leading-relaxed">
        By signing up you agree to our <a href="#" className="text-purple-700 hover:underline">Terms</a> and <a href="#" className="text-purple-700 hover:underline">Privacy Policy</a>.
      </p>
    </form>
  );
};

const ForgotPasswordForm = ({ onClose, onBack }) => {
  const [step, setStep] = useState("request"); // "request" | "reset"
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await forgotPassword(email);
      toast.success("Reset instructions sent", { description: data.message });
      if (data.resetToken) setToken(data.resetToken); // dev convenience
      setStep("reset");
    } catch (err) {
      toast.error("Couldn't send reset instructions", { description: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword({ token, password });
      toast.success("Password reset — you're signed in.");
      onClose();
      navigate("/app/listing-generator");
    } catch (err) {
      toast.error("Reset failed", { description: getErrorMessage(err, "Check the reset token and try again.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-form" className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        data-testid="forgot-back-button"
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-purple-700"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to login
      </button>

      {step === "request" ? (
        <form onSubmit={requestReset} className="space-y-4">
          <div>
            <Label htmlFor="forgot-email" className="text-slate-700 font-semibold text-sm">Email</Label>
            <div className="relative mt-1.5">
              <FieldIcon><Mail className="w-4 h-4" /></FieldIcon>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit-button"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-lg shadow-purple-600/25"
          >
            {loading ? "Sending…" : "Send reset instructions"}
          </Button>
        </form>
      ) : (
        <form onSubmit={submitReset} className="space-y-4">
          <div>
            <Label htmlFor="reset-token" className="text-slate-700 font-semibold text-sm">Reset token</Label>
            <div className="relative mt-1.5">
              <FieldIcon><KeyRound className="w-4 h-4" /></FieldIcon>
              <Input
                id="reset-token"
                data-testid="reset-token-input"
                type="text"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste the token from your email"
                className="h-12 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="reset-password" className="text-slate-700 font-semibold text-sm">New password</Label>
            <div className="mt-1.5">
              <PasswordField
                id="reset-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testid="reset-password-input"
                placeholder="Minimum 6 characters"
                minLength={6}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-button"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-lg shadow-purple-600/25"
          >
            {loading ? "Resetting…" : "Reset password"}
          </Button>
        </form>
      )}
    </div>
  );
};

export const AuthDialog = ({ open, onOpenChange, defaultTab = "login" }) => {
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState("tabs"); // "tabs" | "forgot"
  const close = () => onOpenChange(false);

  const headerCopy = view === "forgot"
    ? { title: "Reset your password", sub: "We'll help you get back into your account." }
    : {
        title: tab === "login" ? "Welcome back" : "Create your account",
        sub: tab === "login" ? "Sign in to manage your EasyLister account." : "Free forever. No credit card needed.",
      };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-dialog"
        className="sm:max-w-md p-0 overflow-hidden rounded-3xl border-purple-100 bg-white max-h-[92vh] flex flex-col gap-0"
      >
        {/* Visually hidden title/desc for a11y */}
        <DialogTitle className="sr-only">Sign in or create an EasyLister account</DialogTitle>
        <DialogDescription className="sr-only">Use email & password or Google to continue.</DialogDescription>

        <div className="relative px-6 sm:px-8 pt-7 pb-2 shrink-0">
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-purple-200/50 blur-3xl pointer-events-none" />
          <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-fuchsia-200/40 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3 pr-8">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center shadow-lg shadow-purple-600/30 shrink-0">
                <Sparkles className="w-4.5 h-4.5" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg font-extrabold text-slate-900 leading-tight">
                  {headerCopy.title}
                </div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">
                  {headerCopy.sub}
                </div>
              </div>
            </div>
          </div>
        </div>

        {view === "forgot" ? (
          <div className="px-6 sm:px-8 pb-8 overflow-y-auto flex-1 min-h-0">
            <ForgotPasswordForm onClose={close} onBack={() => setView("tabs")} />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="px-6 sm:px-8 pb-8 overflow-y-auto flex-1 min-h-0">
            <TabsList
              data-testid="auth-tabs"
              className="grid w-full grid-cols-2 bg-purple-50 rounded-full p-1 h-11"
            >
              <TabsTrigger
                value="login"
                data-testid="auth-tab-login"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow font-semibold"
              >
                Login
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                data-testid="auth-tab-signup"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow font-semibold"
              >
                Sign up
              </TabsTrigger>
            </TabsList>

            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22 }}
              >
                <TabsContent value="login" className="mt-5" forceMount>
                  {tab === "login" && <LoginForm onClose={close} onForgot={() => setView("forgot")} />}
                </TabsContent>
                <TabsContent value="signup" className="mt-5" forceMount>
                  {tab === "signup" && <SignupForm onClose={close} />}
                </TabsContent>
              </motion.div>
            </AnimatePresence>

            <div className="mt-5 text-center text-sm text-slate-600">
              {tab === "login" ? (
                <>
                  New to EasyLister?{" "}
                  <button
                    type="button"
                    onClick={() => setTab("signup")}
                    data-testid="auth-switch-to-signup"
                    className="font-semibold text-purple-700 hover:text-purple-900"
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setTab("login")}
                    data-testid="auth-switch-to-login"
                    className="font-semibold text-purple-700 hover:text-purple-900"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
